import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartShopping,
  faCopy,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  faTwitch,
  faYoutube,
  faTwitter,
  faBluesky,
  faDiscord,
  faInstagram,
  faTiktok,
} from '@fortawesome/free-brands-svg-icons';

const MemberDataGenerator = () => {
  const [memberData, setMemberData] = useState({
    name: '',
    title: '',
    featured: false,
    description: '',
    shortBio: '',
    quote: '',
    links: [],
  });

  const [jsonOutput, setJsonOutput] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [newLink, setNewLink] = useState({
    label: '',
    url: '',
    iconImage: 'faTwitch',
  });

  const iconOptions = [
    { value: 'faTwitch', label: 'Twitch', icon: faTwitch },
    { value: 'faYoutube', label: 'YouTube', icon: faYoutube },
    { value: 'faTwitter', label: 'Twitter', icon: faTwitter },
    { value: 'faDiscord', label: 'Discord', icon: faDiscord },
    { value: 'faInstagram', label: 'Instagram', icon: faInstagram },
    { value: 'faTiktok', label: 'TikTok', icon: faTiktok },
    { value: 'faBluesky', label: 'BlueSky', icon: faBluesky },
    { value: 'faCartShopping', label: 'Merchandise', icon: faCartShopping },
  ];

  useEffect(() => {
    // Generate the image path and update JSON output whenever memberData changes
    if (memberData.name) {
      const folderName = memberData.name.replace(/\s+/g, '').toLowerCase();
      const dataWithImage = {
        ...memberData,
        image: `/members/${folderName}/profile.png`,
      };

      setJsonOutput(JSON.stringify(dataWithImage, null, 2));
    }
  }, [memberData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMemberData({
      ...memberData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleNewLinkChange = (e) => {
    const { name, value } = e.target;
    setNewLink({
      ...newLink,
      [name]: value,
    });
  };

  const addLink = () => {
    if (newLink.label && newLink.url) {
      setMemberData({
        ...memberData,
        links: [...memberData.links, { ...newLink }],
      });
      setNewLink({ label: '', url: '', iconImage: 'faTwitch' });
    }
  };

  const removeLink = (index) => {
    const updatedLinks = [...memberData.links];
    updatedLinks.splice(index, 1);
    setMemberData({
      ...memberData,
      links: updatedLinks,
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonOutput);
    setCopySuccess('Copied!');
    setTimeout(() => setCopySuccess(''), 2000);
  };

  return (
    <div className='container mx-auto px-4 py-8'>
      <h1 className='text-3xl font-bold mb-6 text-primary-DEFAULT'>
        Member Data Generator
      </h1>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
        <div className='bg-white dark:bg-dark-light p-6 rounded-lg shadow-md'>
          <h2 className='text-xl font-semibold mb-4'>Member Information</h2>

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>Name</label>
            <input
              type='text'
              name='name'
              value={memberData.name}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='Member Name'
            />
          </div>

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>
              Title/Tagline
            </label>
            <input
              type='text'
              name='title'
              value={memberData.title}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='Member Title or Tagline'
            />
          </div>

          {/* <div className='mb-4'>
            <label className='flex items-center'>
              <input
                type='checkbox'
                name='featured'
                checked={memberData.featured}
                onChange={handleInputChange}
                className='mr-2'
              />
              <span className='text-sm font-medium'>Featured Member</span>
            </label>
          </div> */}

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>
              Description (Long)
            </label>
            <textarea
              name='description'
              value={memberData.description}
              onChange={handleInputChange}
              rows='4'
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='Detailed description of the member...'
            ></textarea>
          </div>

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>
              Short Bio (For Cards)
            </label>
            <textarea
              name='shortBio'
              value={memberData.shortBio}
              onChange={handleInputChange}
              rows='2'
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='Brief description for member cards...'
            ></textarea>
          </div>

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>Quote</label>
            <input
              type='text'
              name='quote'
              value={memberData.quote}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='A quote from the member'
            />
          </div>

          <h3 className='text-lg font-semibold mt-6 mb-3'>Member Links</h3>

          {memberData.links.length > 0 && (
            <div className='mb-4 space-y-2'>
              {memberData.links.map((link, index) => (
                <div
                  key={index}
                  className='flex items-center p-2 border rounded-md dark:border-gray-700'
                >
                  <div className='flex-grow'>
                    <div className='flex items-center'>
                      <FontAwesomeIcon
                        icon={
                          iconOptions.find(
                            (opt) => opt.value === link.iconImage
                          )?.icon
                        }
                        className='mr-2'
                      />
                      <span className='font-medium'>{link.label}:</span>
                      <span className='ml-2 text-sm truncate'>{link.url}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeLink(index)}
                    className='ml-2 text-red-500 hover:text-red-700'
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className='bg-gray-50 dark:bg-dark-DEFAULT p-4 rounded-md'>
            <h4 className='text-sm font-medium mb-2'>Add New Link</h4>
            <div className='grid grid-cols-1 md:grid-cols-4 gap-2'>
              <div className='md:col-span-1'>
                <input
                  type='text'
                  name='label'
                  value={newLink.label}
                  onChange={handleNewLinkChange}
                  className='w-full px-3 py-2 border rounded-md dark:bg-dark-light dark:border-gray-700'
                  placeholder='Label'
                />
              </div>
              <div className='md:col-span-2'>
                <input
                  type='text'
                  name='url'
                  value={newLink.url}
                  onChange={handleNewLinkChange}
                  className='w-full px-3 py-2 border rounded-md dark:bg-dark-light dark:border-gray-700'
                  placeholder='URL'
                />
              </div>
              <div className='md:col-span-1'>
                <select
                  name='iconImage'
                  value={newLink.iconImage}
                  onChange={handleNewLinkChange}
                  className='w-full px-3 py-2 border rounded-md dark:bg-dark-light dark:border-gray-700'
                >
                  {iconOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={addLink}
              className='mt-2 flex items-center px-4 py-2 bg-primary-DEFAULT  rounded-md hover:bg-primary-dark'
            >
              <FontAwesomeIcon icon={faPlus} className='mr-1' />
              Add Link
            </button>
          </div>
        </div>

        <div className='bg-white dark:bg-dark-light p-6 rounded-lg shadow-md'>
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-xl font-semibold'>Generated JSON</h2>
            <button
              onClick={copyToClipboard}
              className='flex items-center px-3 py-1 bg-primary-DEFAULT  rounded-md hover:bg-primary-dark'
            >
              <FontAwesomeIcon icon={faCopy} className='mr-1' />
              {copySuccess || 'Copy'}
            </button>
          </div>

          <div className='relative'>
            <pre className='p-4 bg-gray-800 text-gray-100 rounded-md overflow-auto max-h-[calc(100vh-300px)]'>
              {jsonOutput || '// Fill out the form to generate JSON'}
            </pre>
          </div>

          <div className='mt-6'>
            <h3 className='text-lg font-semibold mb-2'>Instructions</h3>
            <ol className='list-decimal list-inside space-y-2 text-sm'>
              <li>Fill out all the member information fields</li>
              <li>
                Add links to the member's social media and other platforms
              </li>
              <li>Copy the generated JSON and send it to Akaifuwa</li>
              <li>
                Send a profile image with the dimensions 500px x 500px named{' '}
                <code className='bg-gray-100 dark:bg-dark-DEFAULT px-1 rounded'>
                  profile.png
                </code>{' '}
                also to Akaifuwa
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDataGenerator;
